import React, {useEffect, useRef, useState} from "react";
import {observer} from "mobx-react";
import {Link, NavLink, useLocation} from "react-router-dom";
import {IconName} from "@fortawesome/fontawesome-svg-core";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Tooltip} from "antd";
import {useRequiredStore} from "../../utils/store";
import {RootStoreContext} from "../../stores/root/root.store";
import {RouteConfig, routers} from "../../routers/routers";
import {isAllowed} from "../../utils/helpers";
import {useTranslate} from "../../hooks/translate.hook";
import {Attention, Attentions} from "../../models/attentions/attention";
import {attentionService} from "../../services/attention/attention.service";
import febestFavicon from "./febest_favicon.svg";
import "./navigation.scss";

const NAVIGATION_ITEMS_ID = "primary-navigation-items";
const ATTENTIONS_UPDATE_INTERVAL = 1200000;

interface NavigationItemWithImgProps {
    path: string;
    img: string;
    label: string;
    onClick: () => void;
}

const NavigationItemWithImg: React.FC<NavigationItemWithImgProps> = ({path, img, label, onClick}) => (
    <div className="nav-item menu-logo">
        <Link to={path} className="red" onClick={onClick} aria-label={label}>
            <div className="nav-img-container">
                <img src={img} alt="FMS Logo" />
            </div>
        </Link>
    </div>
);

interface NavigationItemProps {
    path: string;
    name: string;
    iconName?: string;
    text?: string;
    danger?: boolean;
    attention?: Attention | null;
    showTooltip?: boolean;
    onClick: () => void;
}

const NavigationItem: React.FC<NavigationItemProps> = ({
    path,
    name,
    iconName,
    text,
    danger = false,
    attention = null,
    showTooltip = false,
    onClick,
}) => {
    return (
        <div className="nav-item">
            <Tooltip
                title={showTooltip ? name : null}
                placement="right"
                mouseEnterDelay={0.2}
            >
                <NavLink
                    to={path}
                    className={({isActive}) => [
                        "red",
                        danger ? "color-danger" : "",
                        isActive ? "active" : "",
                    ].filter(Boolean).join(" ")}
                    onClick={onClick}
                >
                    <div
                        className="nav-img-container"
                        style={{alignItems: "center", justifyContent: "center"}}
                    >
                        {iconName ? (
                            <FontAwesomeIcon icon={["fas", iconName as IconName]} size="lg" />
                        ) : null}
                        {text ? <span className="nav-initials">{text}</span> : null}
                    </div>
                    <span>{name}</span>
                    {attention && attention.value > 0 ? (
                        <div className={"nav-attentions nav-attentions-" + attention.level}>
                            {attention.value}
                        </div>
                    ) : null}
                </NavLink>
            </Tooltip>
        </div>
    );
};

interface NavigationCollapseButtonProps {
    open: boolean;
    onClick: () => void;
    label: string;
}

const NavigationCollapseButton: React.FC<NavigationCollapseButtonProps> = ({open, onClick, label}) => (
    <div className="nav-item nav-roll-up-menu-button">
        <Tooltip title={label} placement="right">
            <button
                type="button"
                className="red"
                onClick={onClick}
                aria-label={label}
                aria-expanded={open}
                aria-controls={NAVIGATION_ITEMS_ID}
            >
                <div className="nav-img-container">
                    <FontAwesomeIcon
                        icon={["fas", open ? "chevron-left" : "chevron-right"]}
                    />
                </div>
            </button>
        </Tooltip>
    </div>
);

interface NavigationOpenMenuButtonProps {
    open: boolean;
    onClick: () => void;
    label: string;
}

const NavigationOpenMenuButton: React.FC<NavigationOpenMenuButtonProps> = ({open, onClick, label}) => (
    <div className="nav-item nav-open-close-menu-button">
        <button
            type="button"
            className="red"
            onClick={onClick}
            aria-label={label}
            aria-expanded={open}
            aria-controls={NAVIGATION_ITEMS_ID}
        >
            <div className="nav-img-container">
                <FontAwesomeIcon icon={["fas", open ? "times" : "bars"]} size="lg" />
            </div>
        </button>
    </div>
);

interface LeftSideNavigationPanelProps {
    routes: RouteConfig[];
}

const LeftSideNavigationPanel: React.FC<LeftSideNavigationPanelProps> = observer(({routes}) => {
    const t = useTranslate();
    const {pathname} = useLocation();
    const store = useRequiredStore(RootStoreContext);
    const [attentions, setAttentions] = useState<Attentions>({});
    const navigationItemsRef = useRef<HTMLDivElement>(null);
    const menuState = store.menuStore.getStateForMenu;

    useEffect(() => {
        navigationItemsRef.current
            ?.querySelector<HTMLAnchorElement>("a.active")
            ?.scrollIntoView({block: "nearest", inline: "nearest"});
    }, [menuState, pathname]);

    useEffect(() => {
        const syncMenuWithViewport = () => store.menuStore.setViewportWidth(window.innerWidth);

        syncMenuWithViewport();
        window.addEventListener("resize", syncMenuWithViewport);

        return () => window.removeEventListener("resize", syncMenuWithViewport);
    }, [store.menuStore]);

    useEffect(() => {
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                store.menuStore.closeFullScreenMode();
            }
        };

        window.addEventListener("keydown", closeOnEscape);
        return () => window.removeEventListener("keydown", closeOnEscape);
    }, [store.menuStore]);

    useEffect(() => {
        if (!store.menuStore.fullScreenModeState) {
            return;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [store.menuStore.fullScreenModeState]);

    useEffect(() => {
        let active = true;

        const updateAttentions = async () => {
            try {
                const updatedAttentions = await attentionService.getAttentions();
                if (active) {
                    setAttentions(updatedAttentions);
                }
            } catch {
                // A failed badge refresh must not break navigation.
            }
        };

        void updateAttentions();
        const interval = window.setInterval(() => void updateAttentions(), ATTENTIONS_UPDATE_INTERVAL);

        return () => {
            active = false;
            window.clearInterval(interval);
        };
    }, []);

    const closeFullScreenMenu = () => store.menuStore.closeFullScreenMode();
    const fullScreenMenuOpen = store.menuStore.fullScreenModeState;
    const expandedMenuOpen = store.menuStore.openCloseState;

    return (
        <nav
            className={"navigation " + menuState}
            aria-label={t("NAVIGATION.MAIN", {defaultValue: "Main navigation"})}
        >
            <div className="nav-main-container">
                <NavigationItemWithImg
                    path="/"
                    img={febestFavicon}
                    label={t("NAVIGATION.HOME", {defaultValue: "Home"})}
                    onClick={closeFullScreenMenu}
                />
                <NavigationCollapseButton
                    open={expandedMenuOpen}
                    onClick={() => store.menuStore.toggleOpenCloseState()}
                    label={expandedMenuOpen
                        ? t("NAVIGATION.COLLAPSE", {defaultValue: "Collapse navigation"})
                        : t("NAVIGATION.EXPAND", {defaultValue: "Expand navigation"})}
                />
                <div
                    ref={navigationItemsRef}
                    className="nav-items-container"
                    id={NAVIGATION_ITEMS_ID}
                >
                    {routes.map((route) => {
                        if (
                            route.viewInNavigationPanel !== true ||
                            !isAllowed(store.authStore.attributes, route.access)
                        ) {
                            return null;
                        }

                        const isFakedUserPage =
                            route.name === "my_page" && store.authStore.isUserFaked;
                        const userInitials = route.name === "my_page"
                            ? store.authStore.user!.firstName.charAt(0) +
                              store.authStore.user!.lastName.charAt(0)
                            : undefined;

                        return (
                            <NavigationItem
                                key={route.name}
                                path={route.path}
                                name={t(route.systemName)}
                                danger={isFakedUserPage}
                                iconName={route.iconName || undefined}
                                text={userInitials}
                                onClick={closeFullScreenMenu}
                                attention={route.attentionKey ? attentions[route.attentionKey] : null}
                                showTooltip={store.menuStore.isRolledDown}
                            />
                        );
                    })}
                </div>
                <NavigationOpenMenuButton
                    open={fullScreenMenuOpen}
                    onClick={() => store.menuStore.toggleFullScreenModeState()}
                    label={fullScreenMenuOpen
                        ? t("NAVIGATION.CLOSE", {defaultValue: "Close navigation"})
                        : t("NAVIGATION.OPEN", {defaultValue: "Open navigation"})}
                />
            </div>
        </nav>
    );
});

export const LeftSideNavigation = observer(() => {
    const store = useRequiredStore(RootStoreContext);

    return store.authStore.isLoggedIn ? <LeftSideNavigationPanel routes={routers} /> : null;
});

export default LeftSideNavigation;
